import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid
} from "@mui/material";
import { useAddVacancyMutation, useUpdateVacancyMutation } from "../api/vacanciesApi";
import { Vacancy, VacancyFormData, JOB_TYPES, JOB_SITES, EXPERIENCE_LEVELS, EDUCATION_LEVELS, GENDER_PREFERENCES } from "../types/vacancy";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  vacancy?: Vacancy | null;
}

const AddEditVacancy: React.FC<Props> = ({ open, setModalOpen, onSuccess, vacancy }) => {
  // Get tomorrow's date as default deadline
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState<VacancyFormData>({
    job_title: "",
    job_description: "",
    job_type: "",
    job_site: "",
    experience_level: "",
    education: "",
    gender_preference: "Any",
    vacancies: 1,
    deadline: getTomorrowDate()
  });

  const [addVacancy] = useAddVacancyMutation();
  const [updateVacancy] = useUpdateVacancyMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (vacancy) {
      setFormData({
        job_title: vacancy.job_title || "",
        job_description: vacancy.job_description || "",
        job_type: vacancy.job_type || "",
        job_site: vacancy.job_site || "",
        experience_level: vacancy.experience_level || "",
        education: vacancy.education || "",
        gender_preference: vacancy.gender_preference || "",
        vacancies: vacancy.vacancies || 1,
        deadline: vacancy.deadline ? vacancy.deadline.split('T')[0] : ""
      });
    } else {
      setFormData({
        job_title: "",
        job_description: "",
        job_type: "",
        job_site: "",
        experience_level: "",
        education: "",
        gender_preference: "Any",
        vacancies: 1,
        deadline: getTomorrowDate()
      });
    }
  }, [vacancy]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = useCallback(async () => {
    if (!formData.job_title.trim()) {
      setSubmissionError("Job title is required");
      return;
    }

    if (!formData.job_description.trim()) {
      setSubmissionError("Job description is required");
      return;
    }

    if (!formData.deadline) {
      setSubmissionError("Deadline is required");
      return;
    }

    // Check if deadline is after today
    const deadlineDate = new Date(formData.deadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    if (deadlineDate <= today) {
      setSubmissionError("Deadline must be after today");
      return;
    }

    setIsSaving(true);
    setSubmissionError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('job_title', formData.job_title);
      formDataToSend.append('job_description', formData.job_description);
      formDataToSend.append('job_type', formData.job_type);
      formDataToSend.append('job_site', formData.job_site);
      formDataToSend.append('experience_level', formData.experience_level);
      formDataToSend.append('education', formData.education);
      formDataToSend.append('gender_preference', formData.gender_preference);
      formDataToSend.append('vacancies', formData.vacancies.toString());
      formDataToSend.append('deadline', formData.deadline);

      if (vacancy) {
        await updateVacancy({
          id: vacancy.id.toString(),
          body: formDataToSend
        }).unwrap();
      } else {
        await addVacancy({
          body: formDataToSend
        }).unwrap();
      }

      setModalOpen(false);
      onSuccess?.();
    } catch (error: any) {
      setSubmissionError(error?.data?.message || "An error occurred while saving the vacancy");
    } finally {
      setIsSaving(false);
    }
  }, [formData, vacancy, addVacancy, updateVacancy, setModalOpen, onSuccess]);

  const handleClose = () => {
    setModalOpen(false);
    setSubmissionError(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {vacancy ? "Edit Vacancy" : "Add New Vacancy"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {submissionError && (
            <Alert severity="error">{submissionError}</Alert>
          )}

          <TextField
            label="Job Title"
            value={formData.job_title}
            onChange={(e) => handleInputChange('job_title', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Job Description"
            value={formData.job_description}
            onChange={(e) => handleInputChange('job_description', e.target.value)}
            fullWidth
            multiline
            rows={4}
            required
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Job Type</InputLabel>
                <Select
                  value={formData.job_type}
                  onChange={(e) => handleInputChange('job_type', e.target.value)}
                  label="Job Type"
                >
                  {JOB_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Job Site</InputLabel>
                <Select
                  value={formData.job_site}
                  onChange={(e) => handleInputChange('job_site', e.target.value)}
                  label="Job Site"
                >
                  {JOB_SITES.map((site) => (
                    <MenuItem key={site} value={site}>
                      {site}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Experience Level</InputLabel>
                <Select
                  value={formData.experience_level}
                  onChange={(e) => handleInputChange('experience_level', e.target.value)}
                  label="Experience Level"
                >
                  {EXPERIENCE_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Education</InputLabel>
                <Select
                  value={formData.education}
                  onChange={(e) => handleInputChange('education', e.target.value)}
                  label="Education"
                >
                  {EDUCATION_LEVELS.map((edu) => (
                    <MenuItem key={edu} value={edu}>
                      {edu}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Gender Preference</InputLabel>
                <Select
                  value={formData.gender_preference}
                  onChange={(e) => handleInputChange('gender_preference', e.target.value)}
                  label="Gender Preference"
                >
                  {GENDER_PREFERENCES.map((gender) => (
                    <MenuItem key={gender} value={gender}>
                      {gender}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Number of Vacancies"
                type="number"
                value={formData.vacancies}
                onChange={(e) => handleInputChange('vacancies', parseInt(e.target.value) || 1)}
                fullWidth
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>

          <TextField
            label="Application Deadline"
            type="date"
            value={formData.deadline}
            onChange={(e) => handleInputChange('deadline', e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ 
              min: getTomorrowDate() // Prevent selecting today or past dates
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? "Saving..." : (vacancy ? "Update" : "Create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditVacancy;
